import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { API, getAuthHeader } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const EditDialog = ({ r, open, onClose, onSaved }) => {
  const [cats, setCats] = useState({ credit: [], debit: [] });
  const [form, setForm] = useState({ amount: r.amount, direction: r.direction, category: r.category || 'add_to_gross', reason: r.reason });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/talent/payroll-adjustments/categories`, { headers: getAuthHeader() }).then((res) => {
      setCats(res.data);
      setForm((f) => {
        const list = (f.direction === 'credit' ? res.data.credit : res.data.debit) || [];
        return list.some((c) => c.value === f.category) ? f : { ...f, category: list[0]?.value || '' };
      });
    }).catch(() => {});
  }, [open]);
  const catsReady = cats.credit.length > 0;

  const setDir = (direction) => setForm((f) => ({ ...f, direction, category: (direction === 'credit' ? cats.credit : cats.debit)[0]?.value || '' }));
  const save = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/talent/payroll-adjustments/${r.id}/edit`, { ...form, amount: Number(form.amount) }, { headers: getAuthHeader() });
      toast.success(res.data.message); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save - nothing was changed'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="adj-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit adjustment - {r.rider_name}</DialogTitle>
          <DialogDescription>The old adjustment is reversed and the new one applied; the salary slip is regenerated and the change is kept in the history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.direction} onValueChange={setDir} disabled={!catsReady}>
              <SelectTrigger data-testid="adj-edit-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="credit">Credit (pay rider)</SelectItem><SelectItem value="debit">Debit (deduct)</SelectItem></SelectContent>
            </Select>
            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="adj-edit-amount" />
          </div>
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="adj-edit-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>{(form.direction === 'credit' ? cats.credit : cats.debit).map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea rows={3} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Reason (at least 5 characters)" data-testid="adj-edit-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !catsReady || !form.category || !(Number(form.amount) > 0) || (form.reason || '').trim().length < 5} data-testid="adj-edit-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdjustmentRowActions = ({ r, onChanged }) => {
  const [editing, setEditing] = useState(false);
  if (r.status === 'deleted') return <span className="text-[10px] font-semibold text-rose-600">Deleted</span>;

  const del = async () => {
    if (!window.confirm(`Delete this ${r.direction} of AED ${r.amount} for ${r.rider_name}? The payroll and salary slip go back to how they were before it.`)) return;
    try {
      const res = await axios.post(`${API}/talent/payroll-adjustments/${r.id}/delete-applied`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete - nothing was changed'); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`adj-actions-${r.id}`}><MoreVertical className="w-4 h-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setEditing(true)} data-testid={`adj-edit-${r.id}`}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
          <DropdownMenuItem onClick={del} className="text-rose-600" data-testid={`adj-delete-${r.id}`}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {editing && <EditDialog r={r} open={editing} onClose={() => setEditing(false)} onSaved={onChanged} />}
    </>
  );
};
